<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-modal"
      dialog-width="100vw"
      dialog-height="100vh"
    >
      <template #header>
        <BaseIcon name="mdi/card-search-outline" />
        {{ ls({ ja: '検索条件の設定', en: 'Set Search Condition' }) }}
      </template>
      <div class="jps-modal-content">
        <BaseTab :tab-options="tabOptions" v-model="tab">
          <BaseTabPanel val="cs" v-if="isItemDataType" :show-cms="true">
            <div class="tab-padding">
              <div class="jps-field">
                <label class="jps-label">{{ lss('現在の検索', 'Current search') }}</label>
                <span/>
                <UiDataPanel
                  v-if="cs"
                  :nolink="true"
                  type="csearch"
                  :data="cs"
                />
              </div>
              <button class="jps-button" @click="selectCs()">
                {{ lss('使用する検索の選択', 'Select search') }}
              </button>
            </div>
          </BaseTabPanel>
          <BaseTabPanel val="query" v-if="isItemDataType" :show-cms="true">
            <template v-if="ss">
              <SearchBox :ss="ss" :additional="true" />
              <UiItemSearchResult v-if="ss.searched" :ss="ss" />
            </template>
          </BaseTabPanel>
          <BaseTabPanel val="gallery" v-if="!isItemDataType" :show-cms="true">
            <template v-if="ss">
              <SearchBox :ss="ss" />
              <UiCurationSearchResult v-if="ss.searched" :ss="ss" />
            </template>
          </BaseTabPanel>
          <BaseTabPanel val="size" :show-cms="true">
            <div class="tab-padding">
              <div class="jps-field">
                <div class="jps-message">
                  初期段階で表示される検索結果の数です。この数を検索ヒット数より少なくしても、「もっと見る」ボタンを押すことで読み込んで行くことができます。
                </div>
                <label class="jps-label-col" style="width: fit-content">
                  {{ lss('初期表示サイズ', 'Initial search size') }}
                  <input
                    class="jps-input"
                    type="number"
                    min="1"
                    max="200"
                    v-model.number="size"
                  />
                </label>
              </div>

              <div class="jps-field">
                <div class="jps-message">
                  Onにすると、最初に表示されるデータをランダムに並び替えます。続けて読み込んだときの連続性を確保するため、順番は1時間ごとに変わります。
                </div>
                <label class="jps-label" style="width: fit-content">
                  ランダム表示
                  <BaseToggleSwitch v-model="random" />
                </label>
              </div>

              <div class="jps-field">
                <div class="jps-message">
                  検索条件のタイトルです。アウトラインで表示されます。
                </div>
                <label class="jps-label-col" style="width: fit-content">
                  {{ lss('タイトル', 'Title') }}
                  <LangStringEditor
                    class="timeline-title"
                    input-class="jps-ul-input"
                    :value="_def.title"
                    :rule="rule.fields.title"
                  />
                </label>
              </div>
            </div>
          </BaseTabPanel>
        </BaseTab>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()" :disabled="!valid || !loaded">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
      <CsSelectorModal
        ref="csSelectorModal"
        :multiple="false"
        :selected="[]"
        :allow-gallery="false"
        :allow-all-types="true"
      />
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal, CsSelectorModal } from '#components'
import type { TabOption } from '~/components/base/BaseTab.vue'

const { ls, lss } = useI18n()
const { getCustomSearch } = useCustomSearch()
const { searchCuration } = useCuration()
const { getSearchId } = useItem()

interface Props {
  def?: PartListSearchResult
}
const props = withDefaults(defineProps<Props>(), {})

const modal = ref<InstanceType<typeof BaseModal>>()
const _def: Ref<PartListSearchResult> = ref()
const cs: Ref<CustomSearchDefinition> = ref(null)
const ss: Ref<SearchStore<any>> = ref(null)
const query: Ref<SearchQuery> = ref(null)
const size: Ref<number> = ref(7)
const random: Ref<boolean> = ref(false)
const loaded: Ref<boolean> = ref(false)
const openAwait = async (options?: Props) => {
  setOptionsValue(_def, 'def', props, options)

  cs.value = null
  ss.value = null
  query.value = null
  size.value = 7
  random.value = false
  loaded.value = false

  if (_def.value.dataType === 'item') {
    if (_def.value.csId) {
      tab.value = 'cs'
      cs.value = await getCustomSearch(_def.value.csId)
      await setup()
    }
  } else if (_def.value.dataType === 'curation') {
    tab.value = 'gallery'
    cs.value = systemGallerySearchCs()
    ss.value = useSearch(cs.value, searchCuration, {
      configNoCache: true,
      configSyncRoute: false
    })
    ss.value.addAfterSearchListener(result => {
      query.value = ss.value.state.currentQuery
    })
    await ss.value.execute()
  }
  if (_def.value.csId && _def.value.query) {
    query.value = _def.value.query
    size.value = query.value.size
    random.value = query.value.sort?.[0] === 'rand'
    query.value.sort = null
    query.value.size = 20
    ss?.value?.restoreQuery(query.value)
    if (_def.value.dataType === 'item') tab.value = 'query'
    else tab.value = 'gallery'
    loaded.value = true
  } else {
    loaded.value = true
  }
  ensure(_def.value, 'title', {})

  return await modal.value.openAwait<{
    csId: string
    query: any
  }>()
}
defineExpose({ openAwait })

const rule = ref(PART_LIST_SEARCH_RESULT_RULE)
const tab = ref('')

const isItemDataType = computed<boolean>(() => {
  return _def.value.dataType === 'item'
})

const tabOptions = computed((): TabOption[] => {
  if (isItemDataType.value) {
    return [
      {
        val: 'cs',
        label: ls({ ja: '使用するテーマ別検索', en: 'Select Search' }),
      },
      {
        val: 'query',
        label: ls({ ja: '検索条件の設定', en: 'Search Query' }),
        disabled: ss.value == null,
      },
      {
        val: 'size',
        label: ls({ ja: 'その他の設定', en: 'Initial Size' }),
        disabled: ss.value == null,
      },
    ]
  } else {
    return [
      {
        val: 'gallery',
        label: ls({ ja: 'ギャラリーの検索条件の設定', en: 'Search Query' }),
      },
      {
        val: 'size',
        label: ls({ ja: 'その他の設定', en: 'Initial Size' }),
      },
    ]
  }
})

const csSelectorModal = ref<InstanceType<typeof CsSelectorModal>>()
const selectCs = async () => {
  try {
    const result = await csSelectorModal.value.openAwait() as CustomSearchDefinition
    if (!result) return
    cs.value = result
    await setup()
  } catch (e) {}
}

const setup = async () => {
  ss.value = useSearch(cs.value, getSearchId(cs.value.id), {
    configNoCache: true,
    configSyncRoute: false
  })
  ss.value.addAfterSearchListener(result => {
    query.value = ss.value.state.currentQuery
  })
  await ss.value.execute()
}

const valid = computed(() => {
  return query.value
})

const confirm = () => {
  query.value.size = size.value
  query.value.from = 0
  if (random.value) query.value.sort = ['rand']
  modal.value.resolve({
    csId: cs.value.id,
    query: query.value,
  })
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-modal {
  .jps-modal-content {
    .jps-search-box {
      margin-top: 1rem;
    }
  }
}
.tab-padding {
  padding: 1rem 0;
}
</style>
